import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { SettingsSectionProps } from './dashboard.types';
import styles from './Dashboard.module.css';
import { authenticatedFetch } from '../../utils/authenticatedFetch';

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

interface SupervisorInfo {
  supervisor_id: string;
  email: string;
  full_name: string;
  joined_at: string;
}

const SettingsSection = ({ }: SettingsSectionProps) => {
  const { user, updateProfile, sendPasswordResetEmail } = useAuthContext();

  // Profile state
  const [fullName, setFullName] = useState('');
  const [initialName, setInitialName] = useState('');
  const [saving, setSaving] = useState(false);

  // Password reset state
  const [resetting, setResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Supervisor state
  const [supervisor, setSupervisor] = useState<SupervisorInfo | null>(null);
  const [loadingSupervisor, setLoadingSupervisor] = useState(true);
  const [unlinking, setUnlinking] = useState(false);
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  // Initialize form
  useEffect(() => {
    const name = user?.user_metadata?.full_name || '';
    setFullName(name);
    setInitialName(name);
  }, [user]);

  // Fetch supervisor info
  useEffect(() => {
    if (!user?.id) {
      setLoadingSupervisor(false);
      return;
    }
    const fetchSupervisor = async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/user/supervisor?user_id=${user.id}`);
        if (res.ok) {
          const data = await res.json();
          setSupervisor(data.supervisor || null);
        }
      } catch {
        // Ignore errors
      } finally {
        setLoadingSupervisor(false);
      }
    };
    fetchSupervisor();
  }, [user?.id]);

  const handleUnlink = async () => {
    if (!user?.id) return;
    setUnlinking(true);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/user/unlink-supervisor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });
      if (res.ok) {
        setSupervisor(null);
        setShowUnlinkConfirm(false);
        (window as any).showNotification?.('You have been unlinked from your supervisor', 'success');
      } else {
        const data = await res.json();
        (window as any).showNotification?.(data.error || 'Failed to unlink', 'error');
      }
    } catch {
      (window as any).showNotification?.('Failed to unlink from supervisor', 'error');
    } finally {
      setUnlinking(false);
    }
  };

  const hasChanges = fullName.trim() !== initialName;
  const isValidName = fullName.trim().length > 0;

  const handleSave = async () => {
    if (!hasChanges || !isValidName) return;
    setSaving(true);
    const { error } = await updateProfile({ full_name: fullName.trim() });
    setSaving(false);
    if (error) {
      (window as any).showNotification?.('Failed to update profile', 'error');
    } else {
      setInitialName(fullName.trim());
      (window as any).showNotification?.('Profile updated successfully', 'success');
    }
  };

  const handlePasswordReset = async () => {
    if (!user?.email) return;
    setResetting(true);
    const { error } = await sendPasswordResetEmail(user.email);
    setResetting(false);
    if (error) {
      (window as any).showNotification?.('Failed to send reset email', 'error');
    } else {
      setResetSent(true);
      (window as any).showNotification?.('Password reset link sent to your email', 'success');
    }
  };

  return (
    <section className={styles.settingsSection}>
      <h3 className={styles.sectionTitle}>Account Settings</h3>
      <p className={styles.sectionDescription}>Manage your account information</p>

      <div className={styles.settingsContent}>
        {/* Personal Details */}
        <div className={styles.personalDetailsContainer}>
          <div className={styles.personalDetailsContainerHeader}>
            <h4 className={styles.subsectionTitle}>Personal Details</h4>
            <div style={{ width: '100%', height: '1px', background: 'var(--Colors-Neutral-200)', marginTop: '16px' }}></div>
          </div>

          <div className={styles.settingItem}>
            <label>Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Enter your name"
            />
          </div>

          <div className={styles.settingItem}>
            <label>Email Address</label>
            <input
              type="email"
              value={user?.email || ''}
              disabled
              style={{ background: 'rgba(0,0,0,0.03)', color: 'var(--Colors-Neutral-500)', cursor: 'not-allowed' }}
            />
            <span style={{ fontSize: '12px', color: 'var(--Colors-Neutral-500)' }}>
              Email cannot be changed
            </span>
          </div>

          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={!hasChanges || !isValidName || saving}
            style={{ opacity: (!hasChanges || !isValidName || saving) ? 0.5 : 1, cursor: (!hasChanges || !isValidName || saving) ? 'not-allowed' : 'pointer' }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Security */}
        <div className={styles.personalDetailsContainer}>
          <div className={styles.personalDetailsContainerHeader}>
            <h4 className={styles.subsectionTitle}>Security</h4>
            <div style={{ width: '100%', height: '1px', background: 'var(--Colors-Neutral-200)', marginTop: '16px' }}></div>
          </div>

          <p style={{ color: 'var(--Colors-Neutral-600)', fontSize: '14px', margin: 0 }}>
            To change your password, we'll send a reset link to your email address.
          </p>

          <button
            className={styles.saveButton}
            onClick={handlePasswordReset}
            disabled={resetting || resetSent}
            style={{
              opacity: (resetting || resetSent) ? 0.5 : 1,
              cursor: (resetting || resetSent) ? 'not-allowed' : 'pointer',
              background: resetSent ? 'var(--Colors-Neutral-400)' : undefined
            }}
          >
            {resetting ? 'Sending...' : resetSent ? 'Email Sent' : 'Send Password Reset Email'}
          </button>
        </div>

        {/* Supervision */}
        {!loadingSupervisor && supervisor && (
          <div className={styles.personalDetailsContainer}>
            <div className={styles.personalDetailsContainerHeader}>
              <h4 className={styles.subsectionTitle}>Supervision</h4>
              <div style={{ width: '100%', height: '1px', background: 'var(--Colors-Neutral-200)', marginTop: '16px' }}></div>
            </div>

            <p style={{ color: 'var(--Colors-Neutral-600)', fontSize: '14px', margin: 0 }}>
              You are supervised by <strong>{supervisor.full_name || supervisor.email}</strong>
              {supervisor.full_name && <> ({supervisor.email})</>}
            </p>

            <button
              className={styles.saveButton}
              onClick={() => setShowUnlinkConfirm(true)}
              style={{
                background: '#dc3545',
                opacity: unlinking ? 0.5 : 1,
                cursor: unlinking ? 'not-allowed' : 'pointer',
              }}
              disabled={unlinking}
            >
              Leave Supervision
            </button>

            {showUnlinkConfirm && (
              <div style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
              }} onClick={() => setShowUnlinkConfirm(false)}>
                <div style={{
                  background: 'white', borderRadius: '16px', padding: '2rem',
                  maxWidth: '400px', width: '90%', boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                }} onClick={(e) => e.stopPropagation()}>
                  <h4 style={{ margin: '0 0 0.75rem', fontSize: '1.125rem', color: 'var(--Colors-Primary-800)' }}>
                    Leave Supervision?
                  </h4>
                  <p style={{ fontSize: '0.875rem', color: 'var(--Colors-Neutral-600)', lineHeight: 1.5, margin: '0 0 1.5rem' }}>
                    If your subscription was granted by your supervisor, it will be revoked when you leave.
                    Are you sure you want to continue?
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                    <button
                      onClick={() => setShowUnlinkConfirm(false)}
                      style={{
                        padding: '0.5rem 1rem', border: '1px solid var(--Colors-Neutral-200)',
                        borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '0.875rem',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleUnlink}
                      disabled={unlinking}
                      style={{
                        padding: '0.5rem 1rem', border: '1px solid #dc3545',
                        borderRadius: '8px', background: '#dc3545', color: 'white',
                        cursor: unlinking ? 'not-allowed' : 'pointer', fontSize: '0.875rem',
                        opacity: unlinking ? 0.5 : 1,
                      }}
                    >
                      {unlinking ? 'Leaving...' : 'Leave Supervision'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default SettingsSection;
